import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ClipboardList, FileText, Settings as SettingsIcon, LogOut, Server, CheckCircle2, KeyRound, StickyNote, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const nav = [
  { to: "/dashboard", label: "Inicio", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clients" },
  { to: "/trabajos", label: "Pend.", icon: ClipboardList, testid: "nav-jobs" },
  { to: "/realizados", label: "Realiz.", icon: CheckCircle2, testid: "nav-completed" },
  { to: "/presupuestos", label: "Presup.", icon: FileText, testid: "nav-quotes" },
  { to: "/accesos", label: "Accesos", icon: KeyRound, testid: "nav-accesses" },
  { to: "/notas", label: "Notas", icon: StickyNote, testid: "nav-notes" },
  { to: "/ajustes", label: "Ajustes", icon: SettingsIcon, testid: "nav-settings" },
];

// Mobile split: 4 primary tabs in the bottom bar; the rest live in the drawer.
const primaryNav = nav.slice(0, 4);
const drawerNav = nav.slice(4);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 border-r border-border bg-card sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-6 h-16 border-b border-border">
          <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
            <Server className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">JDMTECH</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} className={linkCls}>
              <n.icon className="h-4 w-4" />
              {n.label === "Pend." ? "Pendientes" : n.label === "Realiz." ? "Realizados" : n.label === "Presup." ? "Presupuestos" : n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
          <button onClick={handleLogout} data-testid="logout-button"
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 h-14 border-b border-border bg-card sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
              <Server className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-display font-bold tracking-tight">JDMTECH</span>
          </div>
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button data-testid="menu-button" className="text-muted-foreground p-2" aria-label="Menú">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0 flex flex-col" data-testid="mobile-menu">
              <SheetHeader className="px-5 h-14 flex flex-row items-center border-b border-border">
                <SheetTitle className="font-display">Menú</SheetTitle>
              </SheetHeader>
              <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {nav.map((n) => (
                  <NavLink key={n.to} to={n.to} data-testid={`${n.testid}-drawer`}
                    onClick={() => setMenuOpen(false)} className={linkCls}>
                    <n.icon className="h-4 w-4" />
                    {n.label === "Pend." ? "Pendientes" : n.label === "Realiz." ? "Realizados" : n.label === "Presup." ? "Presupuestos" : n.label}
                  </NavLink>
                ))}
              </nav>
              <div className="p-3 border-t border-border">
                <div className="px-3 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
                <button onClick={handleLogout} data-testid="logout-button-mobile"
                  className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                  <LogOut className="h-4 w-4" /> Cerrar sesión
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1 p-4 sm:p-6 pb-24 md:pb-6 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>

        {/* Mobile bottom nav: 4 primary tabs + drawer trigger */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border grid grid-cols-5">
          {primaryNav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}>
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
          <button onClick={() => setMenuOpen(true)} data-testid="menu-button-bottom"
            className="flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-muted-foreground">
            <Menu className="h-5 w-5" />
            Más
          </button>
        </nav>
      </div>
    </div>
  );
}
